'use client';

import { useInfiniteScroll } from '@/app/[locale]/(home)/_components/Products/utils';
import SkeletonCard from '@/_components/Skeleton/Card';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { resolveBrandId } from '@/_lib/brand-href';
import productService from '@/_service/product.service';
import { IRedux } from '@/_store/types';
import { SkeletonText } from '@/components/ui/skeleton';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import ProductCard from '@/app/[locale]/(home)/where-to-go/[shipper]/[id]/Single/components/Card';

type Props = {
  query: string;
};

const SearchResults: React.FC<Props> = ({ query }) => {
  const t = useTranslations();
  const params = useParams();
  const { menu_id, is_hall, platform_id } = useSelector(
    (state: IRedux) => state.session,
  );
  const { branches } = useSelector((state: IRedux) => state.common);
  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const shipper_id = resolveBrandId(String(params?.shipper || '')) || shipperId;
  const effectiveMenuId =
    is_hall === 'true'
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || '';

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['search-products', query, effectiveMenuId, shipper_id, params?.id],
      queryFn: async ({ pageParam = 1 }) => {
        if (!query?.trim()) {
          return { products: [], count: 0, pageParam };
        }

        const response = await productService.getAll(
          {
            page: pageParam,
            limit: 12,
            search: query.trim(),
            fields:
              'slug,image,categories,description,google_category,barcode,product_type,ikpu,package_code,tags,type,has_modifier',
            product_types: 'simple,combo,origin',
            menu_id: effectiveMenuId,
          },
          shipper_id || '',
          platform_id || '',
        );

        return {
          products: response?.data?.products || [],
          count: response?.data?.count || 0,
          pageParam,
        };
      },
      enabled: Boolean(query?.trim()),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const loadedProductCount = allPages.reduce(
          (total, page) => total + page.products.length,
          0,
        );
        return loadedProductCount < lastPage.count
          ? lastPage.pageParam + 1
          : undefined;
      },
    });

  useInfiniteScroll(
    loadMoreRef,
    !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  );

  const products = data?.pages.flatMap((page) => page.products) || [];
  const totalCount = data?.pages[0]?.count || 0;

  if (isLoading) {
    return (
      <Box>
        <SkeletonText mb={4} noOfLines={1} />
        <SimpleGrid gap={{ base: 3, lg: 4 }} columns={{ base: 2, lg: 3 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </SimpleGrid>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box>
        <Text fontSize='3xl' fontWeight='bold'>
          {t('not_found')}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize='xl' fontWeight={700} lineHeight='1.2' color='text' mb={8}>
        {t('search_results', { count: totalCount })}
      </Text>

      <SimpleGrid columns={{ base: 2, lg: 3 }} gap={4} mb={8}>
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
          />
        ))}
      </SimpleGrid>
      {hasNextPage && (
        <Flex
          ref={loadMoreRef}
          direction='column'
          gap={4}
          mt={4}
          align='stretch'
        >
          {isFetchingNextPage && (
            <SimpleGrid columns={{ base: 2, lg: 3 }} gap={4}>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </SimpleGrid>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default SearchResults;
